import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

const INVALID_TOKEN_FLAG_KEY = "hh_invalid_token_dialog";
const INVALID_TOKEN_REASON_KEY = "hh_invalid_token_reason";
const INVALID_TOKEN_EVENT = "hh-auth-invalid-token";

export const InvalidTokenDialog = () => {
  const location = useLocation();
  const isLogoutPage = location.pathname === "/logout";

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const title = useMemo(() => "Session expired", []);

  const loadFromStorage = useCallback(() => {
    try {
      if (
        isLogoutPage ||
        (typeof window !== "undefined" && window.location.pathname === "/logout")
      ) {
        setOpen(false);
        return;
      }
      const shouldShow = sessionStorage.getItem(INVALID_TOKEN_FLAG_KEY) === "1";
      const storedReason = sessionStorage.getItem(INVALID_TOKEN_REASON_KEY) || "";
      setOpen(shouldShow);
      setReason(storedReason);
    } catch (e) {
      // ignore
    }
  }, [isLogoutPage]);

  useEffect(() => {
    if (isLogoutPage) {
      setOpen(false);
      return;
    }
    loadFromStorage();
    const onEvent = () => {
      // Run after current stack so sessionStorage write from Apollo link is visible.
      requestAnimationFrame(() => loadFromStorage());
    };
    window.addEventListener(INVALID_TOKEN_EVENT, onEvent);
    return () => window.removeEventListener(INVALID_TOKEN_EVENT, onEvent);
  }, [isLogoutPage, loadFromStorage]);

  const handleReload = useCallback(() => {
    try {
      sessionStorage.removeItem(INVALID_TOKEN_FLAG_KEY);
      sessionStorage.removeItem(INVALID_TOKEN_REASON_KEY);
    } catch (e) {
      // ignore
    }

    // Route to /logout (page exists) which will perform Auth0 logout if needed.
    window.location.assign("/logout");
  }, []);

  const handleClose = useCallback(() => {
    // Keep it open until user reloads; closing would leave them on a broken/unauthorized state.
    // If you prefer allowing close, we can enable it later.
  }, []);

  if (isLogoutPage) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock={false}
      slotProps={{
        root: {
          sx: (theme) => ({ zIndex: theme.zIndex.modal + 50 }),
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5 }}>
          Your session is no longer valid. Please reload to sign in again.
        </Typography>
        {reason ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Details: {reason}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReload} variant="contained">
          Reload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

